import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { formatCurrency } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  AlertCircle,
  Check,
  Clock,
  Loader2,
  X
} from "lucide-react";

export default function OverdueConfirmationModal({ isOpen, onClose, onConfirmed }) {
  const { user } = useAuth();
  const [overdueBills, setOverdueBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState(null);
  const currency = user?.currency || "GBP";

  useEffect(() => {
    if (isOpen) {
      fetchOverdueBills();
    }
  }, [isOpen]);

  const fetchOverdueBills = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bills/overdue-unconfirmed`, { withCredentials: true });
      setOverdueBills(response.data.unconfirmed_bills || []);
    } catch (error) {
      console.error("[OverdueModal] Error fetching overdue bills:", error);
      toast.error("Failed to load overdue bills");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (bill, status) => {
    const key = `${bill.bill_id}_${bill.occurrence_date}`;
    setConfirmingId(key);
    
    try {
      await axios.post(`${API}/bills/confirm-overdue`, {
        bill_id: bill.bill_id,
        occurrence_date: bill.occurrence_date,
        status: status
      }, { withCredentials: true });
      
      // Remove from list
      setOverdueBills(prev => prev.filter(b => 
        !(b.bill_id === bill.bill_id && b.occurrence_date === bill.occurrence_date)
      ));
      
      toast.success(status === 'paid' 
        ? `${bill.name} marked as paid` 
        : `${bill.name} marked as still due`
      );
      
      // Notify parent to refresh data
      if (onConfirmed) {
        onConfirmed();
      }
      
    } catch (error) {
      console.error("[OverdueModal] Error confirming bill:", error);
      toast.error("Failed to update bill status");
    } finally {
      setConfirmingId(null);
    }
  };

  const handleClose = () => {
    onClose();
    if (onConfirmed) {
      onConfirmed();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700 text-white max-w-md p-0 overflow-hidden max-h-[85vh]">
        {/* Header */}
        <div className="p-6 pb-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <DialogHeader>
                <DialogTitle className="font-outfit text-lg font-semibold text-white">
                  Review Past Bills
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-400 mt-1">
                Help us keep your Safe to Spend accurate
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : overdueBills.length === 0 ? (
            <div className="text-center py-8">
              <Check className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-300 font-medium">All caught up!</p>
              <p className="text-sm text-slate-400 mt-1">No bills need reviewing</p>
            </div>
          ) : (
            <div className="space-y-3">
              {overdueBills.map((bill) => {
                const key = `${bill.bill_id}_${bill.occurrence_date}`;
                const isConfirming = confirmingId === key;
                
                return (
                  <div 
                    key={key}
                    className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-white">{bill.name}</h4>
                        <p className="text-sm text-slate-400">
                          Due {bill.formatted_date}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-white">
                          {formatCurrency(bill.amount, currency)}
                        </p>
                        <p className="text-xs text-amber-400">
                          {bill.days_overdue} day{bill.days_overdue !== 1 ? 's' : ''} ago
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-400 mb-3">
                      Did you pay this bill?
                    </p>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        onClick={() => handleConfirm(bill, 'paid')}
                        disabled={isConfirming}
                        data-testid={`confirm-paid-${bill.bill_id}`}
                      >
                        {isConfirming ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Paid
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                        onClick={() => handleConfirm(bill, 'still_due')}
                        disabled={isConfirming}
                        data-testid={`confirm-still-due-${bill.bill_id}`}
                      >
                        {isConfirming ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Clock className="w-4 h-4 mr-1" />
                            Still Due
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {overdueBills.length > 0 && !loading && (
          <div className="p-4 border-t border-slate-700 bg-slate-800/50">
            <p className="text-xs text-slate-400 text-center">
              Bills marked as &quot;Still Due&quot; will be included in your Safe to Spend calculation
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
